"use client";

import * as React from "react";
import * as RTooltip from "@radix-ui/react-tooltip";
import { cn } from "./cn";

export type TooltipProps = {
  label: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  delayDuration?: number;
  /** Avoid wrapping in extra `<span>` if child already accepts ref. */
  asChild?: boolean;
};

/**
 * Lightweight tooltip on top of Radix. Uses a portal so the bubble escapes
 * any parent `overflow:hidden/auto/scroll` (e.g. tables, modal bodies) and
 * always renders above other layers.
 */
export function Tooltip({
  label,
  children,
  side = "top",
  align = "center",
  delayDuration = 80,
  asChild = true,
}: TooltipProps) {
  if (label === undefined || label === null || label === "") {
    return <>{children}</>;
  }
  return (
    <RTooltip.Provider delayDuration={delayDuration} skipDelayDuration={50}>
      <RTooltip.Root>
        <RTooltip.Trigger asChild={asChild}>{children}</RTooltip.Trigger>
        <RTooltip.Portal>
          <RTooltip.Content
            side={side}
            align={align}
            sideOffset={6}
            collisionPadding={8}
            className={cn(
              "z-[100] max-w-xs rounded-md bg-ink px-2 py-1 text-[11px] font-medium text-paper shadow-lg",
              "data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out",
            )}
          >
            {label}
            <RTooltip.Arrow className="fill-ink" width={8} height={4} />
          </RTooltip.Content>
        </RTooltip.Portal>
      </RTooltip.Root>
    </RTooltip.Provider>
  );
}
